/**
 * fetch wrapper that attaches the Fabric ID token.
 *
 * Copied into every module. The only per-module difference is the base URL.
 */
import { getIdToken } from "./auth";

const BASE = import.meta.env.VITE_FABRIC_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new ApiError(401, "Not signed in");

  // An absolute URL passes through untouched. The Front Door's search box calls
  // Vault's endpoint directly, and blindly prefixing BASE would build
  // "https://fabric-api…https://vault-api…/search". Both services verify the
  // same Fabric-issued token, so the header is correct either way.
  const url = /^https?:\/\//.test(path) ? path : `${BASE}${path}`;

  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    // The API answers errors as {error}. Fall back to the status text when a
    // proxy or a cold start returns HTML instead, so the UI never shows
    // "Unexpected token < in JSON".
    const detail = await res
      .json()
      .then((j: { error?: string }) => j.error)
      .catch(() => null);
    throw new ApiError(res.status, detail ?? res.statusText);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  get: <T>(path: string) => call<T>("GET", path),
  post: <T>(path: string, body?: unknown) => call<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => call<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => call<T>("PATCH", path, body ?? {}),
  del: <T>(path: string) => call<T>("DELETE", path),
};
